/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Reservations feature flow (top-level, reached from the sidebar/drawer). A
 * list of reservations → a detail page where staff drive the status and open an
 * order from the reservation.
 */

import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { useStatusBarStyle } from '@euler/app/components/AppStatusBar';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { AddVehicleInfoScreen } from '@euler/app/flows/order/pages/AddVehicleInfoScreen';
import { ServiceAgentSelectorScreen } from '@euler/app/flows/order/pages/ServiceAgentSelectorScreen';
import { VehicleBrandSelectorScreen } from '@euler/app/flows/order/pages/VehicleBrandSelectorScreen';
import { VehicleModelSelectorScreen } from '@euler/app/flows/order/pages/VehicleModelSelectorScreen';
import { CreateReservationScreen } from '@euler/app/flows/reservations/pages/create';
import { ReservationDetailScreen } from '@euler/app/flows/reservations/pages/detail';
import { ReservationsListScreen } from '@euler/app/flows/reservations/pages/list';
import { ReservationItemPickerScreen } from '@euler/app/flows/reservations/pages/pick-items';
import { ReservationCenterProvider } from '@euler/app/flows/reservations/provider';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { ErrorFallback } from '@euler/components/error';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ScopedObservableSuspenseResourceContextProvider } from '@euler/utils/hooks';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import {
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { memo } from 'react';

const Stack = createStackNavigator();

const Screens = {
  _main: ReservationsListScreen,
  detail: ReservationDetailScreen,
  _create: CreateReservationScreen,
  _pickReservationItems: ReservationItemPickerScreen,
  _serviceAgentSelector: ServiceAgentSelectorScreen,
  // Borrowed from the order flow so the create form's vehicle picker works
  // inside this stack (brand → model → add-custom chain).
  _vehicleBrandSelector: VehicleBrandSelectorScreen,
  _vehicleModelSelector: VehicleModelSelectorScreen,
  _addVehicleInfo: AddVehicleInfoScreen,
};

export type ReservationsNavParams = ScreenToParams<typeof Screens>;

export const ReservationsFlow = wrapNavigatorScreen(
  () => {
    useStatusBarStyle('dark');
    return <Content />;
  },
  {
    title: '预约',
    headerShown: false,
  },
);

/**
 * App-level reservation detail — a reservations stack opened straight at
 * `detail`, so a "view reservation" from anywhere (AI client_action, a feed
 * thread) pushes onto the ROOT stack and back returns where the user came
 * from rather than into the reservation centre. Mirrors `LeadDetailFlow`;
 * its own sub-navigations still resolve because the whole stack rides along.
 */
export const ReservationDetailFlow = wrapNavigatorScreen(
  ({ id }: { id: string }) => {
    useStatusBarStyle('dark');
    return <Content initialRoute="detail" initialParams={{ id }} />;
  },
  {
    title: '预约',
    headerShown: false,
  },
);

const Content = memo(
  ({
    initialRoute = '_main',
    initialParams = {},
  }: {
    initialRoute?: string;
    initialParams?: Record<string, unknown>;
  }) => {
    const isDesktop = useIsDesktopLayout();
    const { rpx } = useResponsiveContext();
    return (
      <AppNavigationProvider>
        <Sentry.ErrorBoundary
          // @ts-ignore
          fallback={(params: any) => <ErrorFallback {...params} />}
        >
          <ScopedObservableSuspenseResourceContextProvider
            scope={'reservations'}
          >
            <ReservationCenterProvider>
              <BottomSheetModalProvider>
                <Stack.Navigator
                  initialRouteName={initialRoute}
                  screenOptions={{
                    ...TransitionPresets.SlideFromRightIOS,
                    headerMode: 'float',
                    headerTitleAlign: isDesktop ? 'left' : 'center',
                    headerBackTitle: ' ',
                    headerTitleStyle: {
                      fontSize: rpx(isDesktop ? 20 : 18),
                      fontFamily: FontFamily.NotoSans.Bold,
                      color: '#0f172a',
                    },
                    headerStyle: {
                      backgroundColor: '#fff',
                      borderBottomWidth: 1,
                      borderBottomColor: '#e5e7eb',
                      shadowOpacity: 0,
                      elevation: 0,
                    },
                    cardStyle: { backgroundColor: LD.canvas },
                    headerTitle: MultilineHeaderTitle,
                    headerBackImage: () => <BackIcon fill={'#0f172a'} />,
                  }}
                >
                  {spreadScreens(
                    Stack.Screen,
                    Screens,
                    initialRoute,
                    initialParams,
                  )}
                </Stack.Navigator>
              </BottomSheetModalProvider>
            </ReservationCenterProvider>
          </ScopedObservableSuspenseResourceContextProvider>
        </Sentry.ErrorBoundary>
      </AppNavigationProvider>
    );
  },
);
